import { empty, escape, list, object, safeUrl, type Item } from './panel-data';

type Hls = { loadSource(url: string): void; attachMedia(video: HTMLVideoElement): void; destroy(): void; on(event: string, callback: (event: string, data: { fatal?: boolean }) => void): void };
type HlsClass = { new(): Hls; isSupported(): boolean; Events: { ERROR: string } };

/** Try real playback, not HEAD alone: CORS/HEAD rejection does not prove a source is broken. */
export const mountPanelPlayer = (target: HTMLElement, fallbackUrl: string) => {
  let hls: Hls | null = null;
  let timer = 0;
  let generation = 0;
  let signature = '';
  let selectedUrl = '';
  let cleanup = () => {};
  let links: { name: string; url: string }[] = [];
  const failed = new Set<string>();
  const stop = () => {
    generation++;
    window.clearTimeout(timer); timer = 0;
    cleanup(); cleanup = () => {};
    hls?.destroy(); hls = null;
    const video = target.querySelector('video');
    if (video) { video.pause(); video.removeAttribute('src'); video.load(); }
  };
  const fallback = () => {
    stop();
    const url = safeUrl(fallbackUrl);
    target.innerHTML = url ? '<iframe title="Mô phỏng trận đấu" data-gwd-odds-panel-part="stream-frame" data-odds-simulation allowfullscreen></iframe>' : empty('Chưa có nguồn phát cho trận này.');
    if (links.length) {
      const retry = document.createElement('button');
      retry.type = 'button'; retry.className = 'gwd-odds-action'; retry.textContent = 'Thử lại stream';
      retry.setAttribute('data-gwd-odds-panel-part', 'stream-retry');
      retry.onclick = () => { failed.clear(); play(0); };
      target.append(retry);
    }
    const frame = target.querySelector('iframe');
    if (frame) frame.src = url;
  };
  const play = (index: number) => {
    stop();
    const link = links[index];
    if (!link) { fallback(); return; }
    selectedUrl = link.url;
    const current = generation;
    target.innerHTML = `<label data-gwd-odds-panel-part="stream-label">Chọn stream <select data-gwd-odds-panel-part="stream-select" data-odds-stream-select>${links.map((entry, i) => `<option value="${i}">${escape(entry.name)}</option>`).join('')}</select></label><video data-gwd-odds-panel-part="stream-video" controls playsinline muted autoplay preload="auto" aria-label="Livestream trận đấu"></video><p data-gwd-odds-panel-part="stream-status" data-odds-video-status role="status">Đang kết nối stream…</p>`;
    const select = target.querySelector('select')!;
    select.value = String(index);
    select.onchange = () => { failed.clear(); play(Number(select.value)); };
    const video = target.querySelector('video')!;
    video.muted = true;
    const fail = () => {
      if (current !== generation) return;
      failed.add(link.url);
      const next = links.findIndex(entry => !failed.has(entry.url));
      if (next < 0) fallback(); else play(next);
    };
    const arm = () => {
      if (current !== generation || timer) return;
      timer = window.setTimeout(fail, 10000);
    };
    const ready = () => {
      if (current !== generation) return;
      window.clearTimeout(timer); timer = 0;
      const status = target.querySelector('[data-odds-video-status]');
      if (status) status.textContent = '';
    };
    video.addEventListener('error', fail);
    video.addEventListener('canplay', ready);
    video.addEventListener('playing', ready);
    video.addEventListener('waiting', arm);
    video.addEventListener('stalled', arm);
    video.addEventListener('timeupdate', ready);
    cleanup = () => {
      video.removeEventListener('error', fail);
      video.removeEventListener('canplay', ready);
      video.removeEventListener('playing', ready);
      video.removeEventListener('waiting', arm);
      video.removeEventListener('stalled', arm);
      video.removeEventListener('timeupdate', ready);
    };
    arm();
    try {
      const isHls = new URL(link.url).pathname.toLowerCase().endsWith('.m3u8');
      const Constructor = (window as unknown as { Hls?: HlsClass }).Hls;
      if (isHls && !video.canPlayType('application/vnd.apple.mpegurl')) {
        if (!Constructor?.isSupported()) { fail(); return; }
        hls = new Constructor();
        hls.on(Constructor.Events.ERROR, (_event, error) => { if (error.fatal) fail(); });
        hls.attachMedia(video); hls.loadSource(link.url);
      } else { video.src = link.url; video.load(); }
      void video.play().catch(() => { /* Autoplay denial alone is not a broken stream. Native controls remain usable. */ });
    } catch { fail(); }
  };
  const update = (data: Item) => {
    const next = list(object(data.livestreams).links).map((link, index) => ({
      name: String(link.commentator || link.commentatorSource || `Stream ${index + 1}`),
      url: safeUrl(link.url),
    })).filter(link => link.url);
    const nextSignature = JSON.stringify(next);
    if (nextSignature === signature && target.childElementCount) return;
    signature = nextSignature; links = next; failed.clear();
    if (!links.length) fallback();
    else play(Math.max(0, links.findIndex(link => link.url === selectedUrl)));
  };
  return { update, fallback, destroy: () => { stop(); target.replaceChildren(); } };
};
